import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  HTMLAttributes,
} from 'react';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  InfiniteLoader,
  List,
  WindowScroller,
} from 'react-virtualized';
import {
  addPlaceholderProducts,
  fetchProducts,
  setIsLoadingReverse,
  setIsLoadingMore,
} from '../../Actions/ProductListing.action';
import { useDispatch } from 'react-redux';
import ProductListingResizeObserver from './ProductListingResizeObserver';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { ProductListingType } from '../../Reducers/ProductListing.reducer';
import ProductCard from '../ProductCards/ProductCard';
import ProductCardPlaceholder from '../ProductCards/ProductCardPlaceholder';
import ProductCardLandscape from '../ProductCards/ProductCardLandscape';
import LandscapeProductCardPlaceholder from '../ProductCards/ProductCardLandscapePlaceholder';
import { pushEcommerce } from '../../Services/Gtm.service';
import clsx from 'clsx';
import useCssVariables from '../../Hooks/useCssVariables';
import {
  addPlaceholderPricesLoadMore,
  setNavisionListingPricesWithPlaceholders,
} from '../../Actions/NavisionPrices.action';
import {
  addPlaceholderstocksLoadMore,
  setNavisionListingstocksWithPlaceholders,
} from '../../Actions/NavisionStock.action';
import useShowBothPrices from '../../Hooks/useShowBothPrices';

const cache = new CellMeasurerCache({
  defaultWidth: 150,
  minWidth: 70,
  defaultHeight: 600, // TODO denna bör ändras beroende på vilken typ av vy som används på listan
  fixedWidth: true,
  fixedHeight: false,
});

const oneColRowClass = 'grid grid-cols-1 !gap-x-4 pb-4';

const ProductListing = () => {
  const dispatch = useDispatch();

  const cssVars = useCssVariables();

  const {
    products,
    isLoadingMore,
    isLoadingReverse,
    totalProductsCount,
    productsPerRow = 4,
    canLoadMore,
    productListingType = ProductListingType.Grid,
    pageSize,
    location,
    pageNumberForCrawler,
    gtmViewModel,
    navisionPrices = [],
    navisionStocks = [],
  } = useTypedSelector((state) => state.productListing);

  const showBothPrices = useShowBothPrices();
  const { currentType: currentPriceToggleType } = useTypedSelector(
    (state) => state.priceToggle
  );
  const { isInBuyableState } = useTypedSelector(
    (state) => state.buyableState
  );
  const [ready, setReady] = useState(false);
  const rowCount = useMemo(
    () => Math.ceil(products.length / productsPerRow),
    [products.length, productsPerRow]
  );
  let listRef: List | null = null;

  useEffect(() => {
    if (showBothPrices) {
      dispatch(setNavisionListingPricesWithPlaceholders());
    }
    if (currentPriceToggleType != null) {
      dispatch(setNavisionListingstocksWithPlaceholders());
    }
  }, [showBothPrices, currentPriceToggleType]);

  const RowComponent = useCallback(
    ({
      children,
      className,
      ...props
    }: HTMLAttributes<HTMLDivElement>) => (
      <>
        {productListingType == ProductListingType.Grid && (
          <div
            className={clsx(
              className,
              'grid grid-cols-1 gap-4 pb-4 md:grid-cols-3 2xl:grid-cols-4 2xl:gap-6 2xl:pb-6 '
            )}
            {...props}
          >
            {children}
          </div>
        )}
        {productListingType == ProductListingType.List && (
          <div className={clsx(className, oneColRowClass)} {...props}>
            {children}
          </div>
        )}
        {productListingType == ProductListingType.SingleCard && (
          <div className={clsx(className, oneColRowClass)} {...props}>
            {children}
          </div>
        )}
      </>
    ),
    [productListingType]
  );

  const [ProductComponent, ProductPlaceholderComponent] = useMemo(() => {
    switch (productListingType) {
      case ProductListingType.Grid:
      case ProductListingType.SingleCard:
      default:
        return [ProductCard, ProductCardPlaceholder];
      case ProductListingType.List:
        return [ProductCardLandscape, LandscapeProductCardPlaceholder];
    }
  }, [productListingType]);

  useEffect(() => {
    if (!ready) return;
    cache.clearAll();
    listRef?.recomputeRowHeights(0);
  }, [
    listRef,
    ready,
    productListingType,
    products,
    navisionStocks,
    navisionPrices,
  ]);

  useEffect(() => {
    if (!ready) {
      pushEcommerce(gtmViewModel);
    }
    setReady(true);
  }, []);

  const loadMoreClick = () => {
    const productsPerLoad = 24;
    const startIndex = rowCount;
    const endIndex =
      rowCount + Math.ceil(productsPerLoad / productsPerRow);

    dispatch(addPlaceholderProducts(startIndex, endIndex));
    if (showBothPrices) {
      dispatch(addPlaceholderPricesLoadMore(startIndex, endIndex));
    }
    if (currentPriceToggleType != null) {
      dispatch(addPlaceholderstocksLoadMore(startIndex, endIndex));
    }
    dispatch(setIsLoadingMore(true));
  };

  const isRowLoaded = ({ index }: Index) => {
    if (isLoadingReverse) return true;

    return index >= rowCount || !!products[index * productsPerRow];
  };

  const loadMoreRows = ({ startIndex, stopIndex }): Promise<any> => {
    if (startIndex >= rowCount) {
      return new Promise((resolve) => resolve(null));
    }

    const endIndex = stopIndex + 1;
    dispatch(setIsLoadingReverse(true));
    return dispatch(fetchProducts(startIndex, endIndex))
      .then(() => {
        for (let i = startIndex; i < endIndex; i++) {
          cache.clear(i, 0);
        }
        listRef?.recomputeRowHeights(startIndex);
      })
      .finally(() => {
        dispatch(setIsLoadingReverse(false));
      });
  };

  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      const startIndex = index * productsPerRow;
      const endIndex = startIndex + productsPerRow;
      const productsForRow = products.slice(startIndex, endIndex);

      return (
        <CellMeasurer
          cache={cache}
          rowIndex={index}
          columnIndex={0}
          parent={parent}
          key={key}
        >
          <div style={style}>
            <RowComponent key={key}>
              {productsForRow.length > 0 &&
                productsForRow.map((product, idx) =>
                  product ? (
                    <ProductComponent
                      product={product}
                      navisionPrice={navisionPrices?.find(
                        (priceItem) => priceItem?.productId === product?.id
                      )}
                      navisionStock={navisionStocks?.find(
                        (stockItem) => stockItem?.productId === product?.id
                      )}
                      currentPriceToggleType={currentPriceToggleType}
                      showBothPrices={showBothPrices}
                      key={key + idx}
                      lazyLoadImage={true}
                      isVariantListing={
                        !product.variantOptions ||
                        product.variantOptions?.length <= 1
                      }
                      isInBuyableState={isInBuyableState}
                    />
                  ) : (
                    <ProductPlaceholderComponent key={key + idx} />
                  )
                )}
            </RowComponent>
          </div>
        </CellMeasurer>
      );
    },
    [products, productsPerRow, navisionPrices, navisionStocks]
  );

  return (
    <>
      {!ready && (
        <RowComponent className="!gap-4">
          {products.map((product, idx) =>
            product ? (
              <ProductComponent
                product={product}
                navisionPrice={navisionPrices?.find(
                  (priceItem) => priceItem?.productId === product?.id
                )}
                navisionStock={navisionStocks?.find(
                  (stockItem) => stockItem?.productId === product?.id
                )}
                currentPriceToggleType={currentPriceToggleType}
                showBothPrices={showBothPrices}
                key={'not-ready' + idx}
                lazyLoadImage={false}
                isVariantListing={
                  !product.variantOptions ||
                  product.variantOptions?.length <= 1
                }
                isInBuyableState={isInBuyableState}
              />
            ) : (
              <ProductPlaceholderComponent key={'not-ready' + idx} />
            )
          )}
        </RowComponent>
      )}
      {ready && (
        <WindowScroller scrollingResetTimeInterval={0}>
          {({ height, isScrolling, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <InfiniteLoader
                  minimumBatchSize={pageSize / productsPerRow}
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={loadMoreRows}
                  rowCount={rowCount}
                  threshold={5}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      autoHeight
                      ref={(ref) => {
                        if (ref) {
                          registerChild(ref);
                          listRef = ref;
                        }
                      }}
                      height={height}
                      onRowsRendered={onRowsRendered}
                      width={width}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      scrollTop={scrollTop}
                      rowCount={rowCount}
                      rowRenderer={rowRenderer}
                      rowHeight={cache.rowHeight}
                    />
                  )}
                </InfiniteLoader>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
      <LoadMoreButton
        onClick={loadMoreClick}
        disabled={
          !canLoadMore ||
          isLoadingMore ||
          products.length >= totalProductsCount
        }
        isLoading={isLoadingMore}
        count={products.length}
        totalCount={totalProductsCount}
        canLoadMore={canLoadMore}
        location={location}
        pageNumberForCrawler={pageNumberForCrawler}
        pageSize={pageSize}
      />
      <ProductListingResizeObserver />
    </>
  );
};

export default ProductListing;
